/**
 * Performance, Trajectory tab: each building's composite over the trailing quarters.
 * Served from benchmark_composite_history (src/financial/benchmark-history.ts), which
 * records every tenant building's composite per CMS quarter from the first snapshot
 * on; nothing is back-filled, so `real` is false until a quarter is recorded. The +30d
 * projection is computed in the browser from these quarters and labelled MODELED.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { PeerTier } from './performance-types';

/** A CMS methodology change: quarters either side of it are not strictly comparable. */
export interface MethodologyMarker {
  /** Index into `quarters` the vertical sits on. */
  quarterIndex: number;
  /** "Jul 25 survey rule". */
  label: string;
}

/** The benchmark composite (0..100) per building per quarter, oldest first; null where no quarter was recorded. */
export interface CompositeHistory {
  /** Quarter labels, oldest first ("Q3 24" ... "Q2 26"). */
  quarters: string[];
  /** One entry per building; `orgId` is the facility org id (null for a Facility Admin's own building). */
  facilities: { orgId: string | null; composites: (number | null)[] }[];
  /** The peer set's median composite (0..100), for the dashed reference line; null across different peer sets. */
  peerMedian: number | null;
  markers: MethodologyMarker[];
  real: boolean;
}

/** facilities: the facility switcher's pick (operator history only). */
type HistoryQuery = { quarters?: number; tier?: PeerTier; caseMix?: boolean; facilities?: string };

/** GET /api/financial/facilities/benchmarks/history: every building the operator manages. */
export const compositeHistory = route<CompositeHistory, HistoryQuery>()('GET', '/api/financial/facilities/benchmarks/history', { honest: true });
/** GET /api/aithora/my-facility/benchmark/history: a Facility Admin's own building (the session's org). */
export const myCompositeHistory = route<CompositeHistory, HistoryQuery>()('GET', `${AITHORA_PREFIX}/my-facility/benchmark/history`, { honest: true });

export const performanceTrajectoryApi = { compositeHistory, myCompositeHistory } as const;
